import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { PERMISSIONS, getRoleLabel } from "../permissions/permissions";
import chatea from "../assets/chatea.png";
import {
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaUserFriends,
  FaMoneyBill,
  FaChartLine,
  FaUser,
  FaCaretDown,
  FaFileDownload,
  FaUserSecret,
} from "react-icons/fa";
import { TbAffiliate } from "react-icons/tb";
import { MdGroupAdd } from "react-icons/md";
import useImpersonationSession from "../hooks/useImpersonationSession";

const BaseLayout = ({ isAdmin = false }) => {
  const { logout, user, role, hasPermission, isImpersonating } = useAuth();
  const { finalizarSuplantacion } = useImpersonationSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const routePrefix = isAdmin ? "/admin" : "";

  const navItems = !isAdmin
    ? [
        {
          path: `/dashboard`,
          label: "Dashboard",
          icon: <FaChartLine />,
        },
        {
          path: `/referrals`,
          label: "Referidos",
          icon: <FaUserFriends />,
        },
        // {
        //   path: `/payments`,
        //   label: "Pagos",
        //   icon: <FaMoneyBill />,
        // },
        {
          path: `/invite-referrals`,
          label: "Invitar a mi comunidad",
          icon: <MdGroupAdd />,
        },
      ]
    : [
        // rutas admin
        {
          path: "/admin/all-referrals",
          label: "Referidos",
          icon: <FaUserFriends />,
        },
        {
          path: "/admin/all-referral-codes",
          label: "Códigos",
          icon: <TbAffiliate />,
        },
        // {
        //   path: "/admin/payment-approval",
        //   label: "Aprobaciones",
        //   icon: <TbAffiliate />,
        // },
        {
          path: "/admin/payment-file",
          label: "Archivo de Pagos",
          icon: <FaFileDownload />,
          permission: PERMISSIONS.VIEW_PAYMENT_FILE,
        },
        {
          path: "/admin/impersonate",
          label: "Suplantar usuario",
          icon: <FaUserSecret />,
          permission: PERMISSIONS.IMPERSONATE_USER,
        },
      ].filter((item) => !item.permission || hasPermission(item.permission));

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth > 768) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isAdmin && location.pathname === "/admin") {
      navigate("/admin/profile");
    }
  }, [isAdmin, location.pathname, navigate]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    setIsDropdownOpen(false);
  };

  const navigateToProfile = () => {
    navigate(`${routePrefix}/profile`);
    setIsDropdownOpen(false);
  };

  const closeSidebar = () => {
    if (windowWidth <= 768) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <div className={`layout ${isAdmin ? "admin-layout" : ""}`}>
      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )}

      {/* Sidebar móvil */}
      <div className={`sidebar ${isSidebarOpen ? "open" : ""}`}>
        <div className="sidebar__header">
          <div className="sidebar__logo">
            <Link
              to={isAdmin ? "/admin/profile" : "/dashboard"}
              onClick={closeSidebar}
            >
              <img
                src={chatea}
                className="header__logo--img-mobile"
                alt="Logo"
              />
            </Link>
          </div>
          <button className="sidebar__close" onClick={toggleSidebar}>
            <FaTimes />
          </button>
        </div>

        <div className="sidebar__user">
          <div className="user-info">
            {user?.email || "example@gmail.com"}
            {isAdmin && (
              <span className="admin-badge">{getRoleLabel(role)}</span>
            )}
            {isImpersonating && (
              <span className="admin-badge admin-badge--impersonation">
                Suplantando
              </span>
            )}
          </div>
        </div>

        <nav className="sidebar__nav">
          <Link
            to={`${routePrefix}/profile`}
            className={
              location.pathname === `${routePrefix}/profile` ? "active" : ""
            }
            onClick={closeSidebar}
          >
            <FaUser />
            <span>Información personal</span>
          </Link>

          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={location.pathname === item.path ? "active" : ""}
              onClick={closeSidebar}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}

          {isImpersonating && (
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                closeSidebar();
                finalizarSuplantacion(null);
              }}
              className="user-dropdown__option user-dropdown__option--impersonation"
            >
              <FaUserSecret />
              <span>Volver al panel admin</span>
            </Link>
          )}

          <Link
            to="#"
            onClick={(e) => {
              e.preventDefault();
              handleLogout();
              closeSidebar();
            }}
            className="user-dropdown__option logout"
          >
            <FaSignOutAlt />
            <span>Cerrar Sesión</span>
          </Link>
        </nav>
      </div>

      {/* Header para desktop */}
      <header className="header">
        <div className="header__content">
          <div className="header__left">
            <button className="menu-toggle" onClick={toggleSidebar}>
              <FaBars />
            </button>
            <div className="header__logo">
              <Link to={isAdmin ? "/admin/profile" : "/dashboard"}>
                <img className="header__logo--img" src={chatea} alt="Logo" />
              </Link>
            </div>
          </div>

          <nav className="header__nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={location.pathname === item.path ? "active" : ""}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="header__user">
            <div className="user-dropdown" ref={dropdownRef}>
              <button
                className="user-dropdown__button"
                onClick={toggleDropdown}
              >
                {user?.email || "example@gmail.com"}
                {isAdmin && (
                  <span className="admin-badge">{getRoleLabel(role)}</span>
                )}
                {isImpersonating && (
                  <span className="admin-badge admin-badge--impersonation">
                    Suplantando
                  </span>
                )}
                <FaCaretDown className={isDropdownOpen ? "rotate" : ""} />
              </button>
              <div
                className={`user-dropdown__menu ${
                  isDropdownOpen ? "open" : ""
                }`}
              >
                <button
                  onClick={navigateToProfile}
                  className="user-dropdown__option"
                >
                  <FaUser />
                  <span>Información personal</span>
                </button>
                {isImpersonating && (
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      finalizarSuplantacion(null);
                    }}
                    className="user-dropdown__option user-dropdown__option--impersonation"
                  >
                    <FaUserSecret />
                    <span>Volver al panel admin</span>
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="user-dropdown__option logout"
                >
                  <FaSignOutAlt />
                  <span>Cerrar Sesión</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="main">
        <div className="container">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default BaseLayout;
